import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import WWSApiService, {
  AiTextGenerationPresetOption,
  customPromptPresetOption,
  GenerateTextContext,
  GenerateTextResponse,
} from '../services/wws-api/wws-api.service';
import { getUrlPageItemPaths } from '../glm/utils/url';
import axios, { AxiosError } from 'axios';

export interface IAiTextGenerationContext {
  presetOptions: AiTextGenerationPresetOption[];
  generatedText: string;
  isThinking: boolean;
  modalOpen: boolean;
  selectedOption: string;
  errorMessage?: string | null;
  isRetryableError?: boolean | null;
  generatedTextHistory?: AiTextGenerationHistoryItem[];
  field?: AIFieldProps;
  setField: React.Dispatch<React.SetStateAction<any>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedOption: (option: string) => void;
  retryRequest: () => void;
  isModalInZeroState: boolean;
  activeHistoryPage: number;
  updateActiveHistoryPage: (targetHistoryPage: number) => void;
}

export interface AIFieldProps {
  fieldText: string;
  fieldName: string;
  setFieldText: (fieldName: string, text: string) => void;
  relatedFields?: Record<string, string>;
}

export interface AiTextGenerationHistoryItem extends GenerateTextResponse {
  presetOption: string;
}

const AiTextGenerationContext = createContext<IAiTextGenerationContext | null>(null);

interface AiTextGenerationProviderProps {
  children: React.ReactNode;
}

const NON_RETRYABLE_STATUS_CODES = [400, 401, 413, 429];

export const AiTextGenerationProvider: React.FC<AiTextGenerationProviderProps> = ({ children }) => {
  const [presetOptions, setPresetOptions] = useState<AiTextGenerationPresetOption[]>([]);
  // For display: which option generated the current text
  const [activeOption, setActiveOption] = useState<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const [generatedText, setGeneratedText] = useState<string>('');
  const [activeHistoryPage, setActiveHistoryPage] = useState<number>(0);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [field, _setField] = useState<AIFieldProps>({
    fieldText: '',
    fieldName: '',
    setFieldText: () => {},
  });
  const fieldRef = useRef(field);

  const setField = (fieldArg: AIFieldProps) => {
    fieldRef.current = fieldArg;
    _setField(fieldArg);
  };

  useEffect(() => {
    fieldRef.current = field;
  }, [field]);

  // push generated results into an array if we want to keep history
  const [generatedTextHistory, setGeneratedTextHistory] = useState<AiTextGenerationHistoryItem[]>([]);

  useEffect(() => {
    WWSApiService.getPresets().then(options => {
      setPresetOptions(options);
    });
  }, []);

  const generateText = useCallback(
    async (
      promptInput: string,
      input: string,
      conversationId?: string,
      fieldName?: string,
      relatedFields?: Record<string, string>,
      abortSignal?: AbortSignal
    ) => {
      const { pageId, pageItemType } = getUrlPageItemPaths();

      if (!pageId || !pageItemType) {
        throw new Error('Invalid page context for text generation.');
      }

      const isPromptInputAPreset = presetOptions.find(option => option.value === promptInput);
      const context: GenerateTextContext = {
        conversationId: conversationId,
        text: input,
        context: {
          pageId,
          pageItemType,
          fieldName: fieldName || 'content',
          relatedFields: relatedFields || {},
        },
        ...(isPromptInputAPreset ? { presetIdentifier: promptInput } : { userInstructions: promptInput }),
      };

      return WWSApiService.generateText(context, abortSignal);
    },
    [presetOptions]
  );

  // Check if error is retryable based on status code
  const isRetryableError = errorStatusCode ? !NON_RETRYABLE_STATUS_CODES.includes(errorStatusCode) : true;

  const resetResponseRound = useCallback(() => {
    setGeneratedText('');
    setIsThinking(false);
    setErrorMessage(null);
    setErrorStatusCode(null);
  }, []);

  const resetContext = useCallback(() => {
    resetResponseRound();
    setGeneratedTextHistory([]);
    setActiveHistoryPage(0);
    setConversationId(undefined);
    setActiveOption('');
  }, [resetResponseRound]);

  const updateActiveHistoryPage = (targetHistoryPage: number) => {
    if (targetHistoryPage < 0 || targetHistoryPage >= generatedTextHistory.length) {
      return;
    }
    const historyItem = generatedTextHistory[targetHistoryPage];
    setGeneratedText(historyItem.generatedText);
    setConversationId(historyItem.conversationId);
    setActiveHistoryPage(targetHistoryPage);
  };

  useEffect(() => {
    if (!modalOpen && abortControllerRef.current) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    }

    if (!modalOpen) {
      resetContext();
    }
  }, [modalOpen, resetContext]);

  const retryRequest = () => {
    if (!errorMessage) return;
    selectOption(activeOption);
  };

  // Public API to select an option (can be called multiple times with same value)
  const selectOption = useCallback(
    (option: string) => {
      setActiveOption(option);
      // If its an empty custom prompt, do nothing
      if (!option || option === customPromptPresetOption.value) {
        return;
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      resetResponseRound();
      setIsThinking(true);

      generateText(
        option,
        generatedText || fieldRef.current.fieldText,
        conversationId,
        fieldRef.current.fieldName,
        fieldRef.current.relatedFields,
        abortController.signal
      )
        .then(data => {
          const newHistoryItem: AiTextGenerationHistoryItem = {
            ...data,
            presetOption: option,
          };
          setGeneratedTextHistory(prev => [...prev, newHistoryItem]);
          setActiveHistoryPage(generatedTextHistory.length);
          setGeneratedText(data.generatedText);
          setConversationId(data.conversationId);
        })
        .catch((error: AxiosError) => {
          if (axios.isCancel(error)) {
            return;
          }
          const errorResponse = (error.response as unknown) as
            | { data: { message?: string; status?: number } }
            | undefined;
          const errorDataMessage =
            errorResponse?.data?.message || error.response?.statusText || 'Failed to generate text. Please try again.';
          setErrorMessage(errorDataMessage);
          const errorDataStatus = errorResponse?.data?.status || error.response?.status || 500;
          setErrorStatusCode(errorDataStatus);
        })
        .finally(() => {
          if (abortControllerRef.current === abortController) {
            setIsThinking(false);
            abortControllerRef.current = null;
          }
        });
    },
    [conversationId, resetResponseRound, generateText, generatedText, generatedTextHistory.length]
  );

  return (
    <AiTextGenerationContext.Provider
      value={{
        modalOpen,
        presetOptions,
        generatedText,
        isThinking,
        selectedOption: activeOption,
        errorMessage,
        generatedTextHistory,
        field,
        setField,
        setModalOpen,
        setSelectedOption: selectOption,
        retryRequest,
        isRetryableError,
        isModalInZeroState: activeOption === customPromptPresetOption.value,
        activeHistoryPage,
        updateActiveHistoryPage,
      }}
    >
      {children}
    </AiTextGenerationContext.Provider>
  );
};

export const useAiTextGenerationContext = () => {
  const context = useContext(AiTextGenerationContext);

  if (!context) {
    throw new Error('useAiTextGenerationContext must be used within AiTextGenerationProvider');
  }

  return context;
};
